import { useApiContext } from '@/contexts/apiContext';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LaunchIcon from '@mui/icons-material/Launch';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Button, Chip, CircularProgress, Divider, Stack, Typography, alpha, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type Props = {
  surveyId: string;
  responseId: string;
  userResponses: Record<string, any>;
  maxHeight?: number;
};

const StepComplete = ({ surveyId, responseId, userResponses, maxHeight }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const navigate = useNavigate();
  const theme = useTheme();

  const [containerRef] = useElementSize();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [dataSource, setDataSource] = useState<any>(null);
  const [userAppInstance, setUserAppInstance] = useState<any>(null);

  useEffect(() => {
    const getSurveyPipelineItems = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getUserSurveyProcessingResult(surveyId, responseId);
        if (response && response.items) {
          setDataSource(response.items.find((item: any) => item.entityType === 'dataSourceInstance'));
          setUserAppInstance(response.items.find((item: any) => item.entityType === 'userAppInstance'));
        }
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (surveyId && responseId) {
      getSurveyPipelineItems();
    }
  }, [surveyId, responseId]);

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        p: 3,
      }}
      ref={containerRef}
    >
      <Stack spacing={3}>
        {/* Success header */}
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <CheckCircleOutlineIcon
            sx={{ fontSize: 40, color: theme.palette.success?.main || 'success.main', flexShrink: 0, mt: 0.25 }}
          />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              Your pipeline is ready!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Your agentic pipeline has been created and is ready to use.
            </Typography>
          </Box>
        </Stack>

        <Divider />

        {/* Next steps */}
        <Stack spacing={1.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Next steps
          </Typography>

          {userResponses.dataSourceType === 'userUpload' && (
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Chip
                size="small"
                label="1"
                sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.15), color: 'secondary.dark', fontWeight: 700, minWidth: 28 }}
              />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Upload your documents
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Open the data source to upload the files you want to process.
                </Typography>
              </Box>
            </Stack>
          )}

          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Chip
              size="small"
              label={userResponses.dataSourceType === 'userUpload' ? '2' : '1'}
              sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.15), color: 'secondary.dark', fontWeight: 700, minWidth: 28 }}
            />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                View your results
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Open the app to see the output from your pipeline.
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Divider />

        {/* Action buttons */}
        <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="flex-end">
          <Button
            variant="text"
            onClick={goToDashboard}
            startIcon={<DashboardIcon fontSize="small" />}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            {t('Go to dashboard')}
          </Button>
          {!loading && dataSource && dataSource.dataSourceDef && (
            <Button
              variant="outlined"
              color="secondary"
              href={`/dataSourceInstance/${dataSource.dataSourceDef.id}/${dataSource.id}`}
              target="_blank"
              startIcon={<UploadFileIcon fontSize="small" />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                px: 2.5,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {t('Open data source')}
            </Button>
          )}
          {userAppInstance && (
            <Button
              variant="contained"
              color="secondary"
              href={`/userAppInstance/${userAppInstance.userAppDef.id}/${userAppInstance.id}`}
              target="_blank"
              startIcon={<LaunchIcon fontSize="small" />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                px: 2.5,
                color: 'text.primary',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {t('Open app')}
            </Button>
          )}
          {loading && <CircularProgress size={24} color="secondary" />}
        </Stack>
      </Stack>
    </Box>
  );
};

export default StepComplete;
